/**
 * The URLs that open the admin on a particular set of rows.
 *
 * Shared, because both sides need the same rule: the morning brief writes these
 * into a Discord message, and the dashboard's cards navigate to them. Two copies
 * would drift, and the drift is invisible — a link that opens an *unfiltered*
 * table looks like a working link, and the reader concludes the queue is bigger
 * than it is.
 *
 * ── The format is the admin's, not ours ──────────────────────────────────────
 * The collection list reads its filters straight out of the query string, one
 * `<field>_op` / `<field>_value` pair per field (`@rebasepro/app`'s
 * `useDataTableController`, `parseFilterAndSort`). `==` encodes to `%3D%3D`.
 * That is a contract with a package this app does not own, so it is written down
 * once, here, and pinned by `backend/test/links.test.ts` — a rename upstream
 * must break a test rather than silently produce links that open the wrong set.
 */
/** The query string the admin's collection list parses back into filters. */
export function encodeFilter(filter) {
    const parts = [];
    for (const [field, [op, value]] of Object.entries(filter)) {
        parts.push(`${encodeURIComponent(`${field}_op`)}=${encodeURIComponent(op)}`);
        parts.push(`${encodeURIComponent(`${field}_value`)}=${encodeURIComponent(String(value))}`);
    }
    return parts.join("&");
}
/**
 * The in-app path for a collection list, filtered —
 * `/c/leads?status_op=%3D%3D&status_value=new`.
 *
 * Relative: this is what a router navigates to. The brief wraps it in an
 * absolute origin because a message read in Discord has no page to be relative
 * to.
 */
export function collectionPath(slug, filter) {
    const query = filter ? encodeFilter(filter) : "";
    return `/c/${slug}${query ? `?${query}` : ""}`;
}
/** One row, open — `/c/agent_runs/<id>`. */
export function entityPath(slug, id) {
    return `/c/${slug}/${encodeURIComponent(id)}`;
}
